import { AUTH_TOKEN_KEY } from '@assets/constants'
import { HttpRepository, HttpResponse } from '@domain/repositories/HttpRepository'
import { ErrorFromStatus, ServerError, NotFoundError } from '@microservices/errors/http'
import axios, { AxiosInstance, AxiosError } from 'axios'
import { LocalStorage } from './LocalStorage'

const headers = {
  'Content-Type': 'application/json',
}

const localStorage = new LocalStorage()

export class Http implements HttpRepository {
  private api: AxiosInstance
  constructor(baseURL: string) {
    this.api = axios.create({
      baseURL,
      headers,
    })
    this.setUpInterceptor()
  }

  setUpInterceptor() {
    this.api.interceptors.request.use(
      async (config) => {
        const token = await localStorage.getItem(AUTH_TOKEN_KEY)
        if (token && config.headers) config.headers.Authorization = `Bearer ${token}`
        return config
      },
      (error) => {
        Promise.reject(error)
      }
    )
  }

  async post<T>(
    path: string,
    params?: Record<string, any> | undefined,
    config?: any
  ): Promise<HttpResponse<T> | undefined> {
    try {
      const response = await this.api.post(path, params, { ...config })
      return {
        status: response.status,
        data: response.data as T,
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        const errorFromStatus = ErrorFromStatus(error.response?.status)
        if (errorFromStatus) throw errorFromStatus
        throw new ServerError()
      }
    }
  }

  async get<T>(
    path: string,
    params?: Record<string, any> | undefined,
    config?: any
  ): Promise<HttpResponse<T> | undefined> {
    try {
      const response = await this.api.get(path, { ...config, params })
      return {
        status: response.status,
        data: response.data as T,
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        const errorFromStatus = ErrorFromStatus(error.response?.status)
        if (errorFromStatus) throw errorFromStatus
        throw new ServerError()
      }
    }
  }

  async patch<T>(
    path: string,
    params?: Record<string, any> | undefined,
    config?: any
  ): Promise<HttpResponse<T> | undefined> {
    try {
      const response = await this.api.patch(path, params, { ...config })
      return {
        status: response.status,
        data: response.data as T,
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 404) throw new NotFoundError()
        if (error.response?.status === 500) throw new ServerError()
      }
    }
  }
}
